import { Component, Inject, OnInit } from '@angular/core';
import moment from 'moment';
import { UtilsService } from '../../../shared/utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiRequestService } from '../../../shared/api-request.service';
import { AppService } from '../../../app.service';
import { AssetsService } from '../../assets.service';
import { NgForm } from '@angular/forms';
import {
  NetworkedAccountsSelectorComponent
} from '../../../shared/networked-accounts-selector/networked-accounts-selector.component';
import { VendorsSelectorComponent } from '../../../shared/vendors-selector/vendors-selector.component';
import {
  NetworkedUsersSelectorComponent
} from '../../../shared/networked-users-selector/networked-users-selector.component';
import { AssetMaintenanceScheduleModel } from '../../../models/asset-maintenance-schedule.model';
import { faHardHat } from '@fortawesome/free-solid-svg-icons';
import { AssetMaintenanceRecordsService } from '../../asset-maintenance-records/asset-maintenance-records.service';
import { AssetMaintenanceSchedulesService } from '../asset-maintenance-schedules.service';
import { SchedulerComponent } from '../../../shared/scheduler/scheduler.component';
import { SchedulerDurationUnit, SchedulerFormValue, SchedulerValue } from '../../../shared/scheduler/scheduler.interface';
import { SchedulerService } from '../../../shared/scheduler/scheduler.service';

@Component({
  selector: 'app-asset-maintenance-schedules-edit',
  templateUrl: './asset-maintenance-schedules-edit.component.html',
  styleUrls: ['./asset-maintenance-schedules-edit.component.scss']
})
export class AssetMaintenanceSchedulesEditComponent implements OnInit {

  inProgress: boolean = false;
  asset_maintenance_schedule: AssetMaintenanceScheduleModel = new AssetMaintenanceScheduleModel();
  faHardHat = faHardHat;

  maintenanceTypeOptions = this.utils.toOptions(this.assetMaintenanceRecordsService.TYPES);
  priorityOptions = this.utils.toOptions(this.assetMaintenanceRecordsService.PRIORITIES);
  scheduleModeOptions = this.utils.toOptions(this.assetMaintenanceSchedulesService.SCHEDULE_MODES);
  autoCreateLeadTimeUnitOptions = this.utils.toOptions(this.assetMaintenanceSchedulesService.AUTO_CREATE_LEAD_TIME_UNITS);

  nextDueAtDateTime: moment.Moment | null;

  today: moment.Moment;

  duration_value: number | null = 1;
  duration_unit: SchedulerDurationUnit = 'hours';

  constructor(
    public utils: UtilsService,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dialogRef: MatDialogRef<AssetMaintenanceSchedulesEditComponent>,
    private api: ApiRequestService,
    public app: AppService,
    public assetsService: AssetsService,
    public assetMaintenanceRecordsService: AssetMaintenanceRecordsService,
    public assetMaintenanceSchedulesService: AssetMaintenanceSchedulesService,
    public schedulerService: SchedulerService
  ) { }

  ngOnInit() {
    const asset_id = this.dialogData?.asset_id;
    const asset_maintenance_schedule_id = this.dialogData?.asset_maintenance_schedule_id;

    this.today = moment()
      .tz(this.app.account.timezone);

    const duration = this.schedulerService.getDurationFormValue(this.asset_maintenance_schedule.duration_minutes);
    this.duration_value = duration.value;
    this.duration_unit = duration.unit;

    if (typeof asset_id === 'number' && asset_id > 0) {
      this.asset_maintenance_schedule.asset_id = asset_id;
    }

    if (typeof asset_maintenance_schedule_id === 'number' && asset_maintenance_schedule_id > 0) {
      this.find(asset_maintenance_schedule_id);
    }
  }

  /**
   * Finds an Asset Maintenance Schedule by its ID.
   *
   * @param {number} id - The ID of the Asset Maintenance Schedule.
   * @return {void}
   */
  private find(id: number): void {
    // Make the API request.
    this.api.makeRequest('get', `v2/assets/${this.asset_maintenance_schedule.asset_id}/maintenance-schedules/${id}`)
      .then((response) => {
        this.asset_maintenance_schedule = response.data as AssetMaintenanceScheduleModel;

        // Extract User ids.
        this.asset_maintenance_schedule.user_ids = this.asset_maintenance_schedule.users ? this.asset_maintenance_schedule.users.map((user) => user.id) : [];

        // Convert the unix "next_due_at" timestamp to a moment date/time object using the account timezone.
        if ( this.asset_maintenance_schedule.next_due_at ) {
          this.nextDueAtDateTime = moment.unix(this.asset_maintenance_schedule.next_due_at).tz(this.app.account.timezone);
        }

        // Initialise duration fields using the loaded maintenance schedule.
        const duration = this.schedulerService.getDurationFormValue(
          this.asset_maintenance_schedule.duration_minutes
        );
        this.duration_value = duration.value;
        this.duration_unit = duration.unit;

        this.onAutoCreateLeadTimeValueBlur();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Handles form submission by checking if the form is valid and whether the submit button has already been pressed.
   * If the form is invalid, a form validation error message is displayed.
   *
   * @param {NgForm} form - The NgForm object representing the form to be submitted.
   * @param {boolean} [autoCloseDialog=true] - Flag indicating whether to automatically close the dialog after submission.
   * @return {void}
   */
  onSubmit(form: NgForm, autoCloseDialog: boolean = true): void {
    // Check if the button was already pressed.
    if ( this.inProgress ) {
      return;
    }

    // Check form validation.
    if ( !form.valid ) {
      this.utils.showFormValidationError('Please enter all required features.');
      return;
    }

    // Check Default Priority validation.
    const validDefaultPriorities = Object.keys(this.assetMaintenanceRecordsService.PRIORITIES);
    if (!validDefaultPriorities.includes(this.asset_maintenance_schedule.default_priority)) {
      this.utils.showFormValidationError('Please select a valid Default Priority.');
      return;
    }

    // Check Maintenance Type Other validation.
    if ( this.asset_maintenance_schedule.maintenance_type !== 'other' ) {
      const validMaintenanceTypes = Object.keys(this.assetMaintenanceRecordsService.TYPES);

      if (!validMaintenanceTypes.includes(this.asset_maintenance_schedule.maintenance_type)) {
        this.utils.showFormValidationError('Please select a valid Maintenance Type.');
        return;
      }
    }

    // Check Maintenance Type Other validation.
    if (
      this.asset_maintenance_schedule.maintenance_type === 'other' &&
      (!this.asset_maintenance_schedule.maintenance_type_other || !this.asset_maintenance_schedule.maintenance_type_other.trim())
    ) {
      this.utils.showFormValidationError('Please enter the other Maintenance Type.');
      return;
    }

    // Check Next Due At validation for non-recurring schedules.
    if ( !this.asset_maintenance_schedule.is_recurring && !this.isNextDueAtValid ) {
      this.utils.showFormValidationError('Please select a valid Next Due At date and time.');
      return;
    }

    // Check recurring Schedule validation.
    if ( this.asset_maintenance_schedule.is_recurring ) {
      if (!this.asset_maintenance_schedule.rrule || !this.asset_maintenance_schedule.schedule_start_at) {
        this.utils.showFormValidationError('Please configure the recurring schedule.');
        return;
      }

      // Check that Schedule End is after Schedule Start.
      if (
        this.asset_maintenance_schedule.schedule_end_at &&
        this.asset_maintenance_schedule.schedule_end_at < this.asset_maintenance_schedule.schedule_start_at
      ) {
        this.utils.showFormValidationError('Schedule End must be after Schedule Start.');
        return;
      }
    }

    // Check Auto Create lead time validation.
    if ( this.asset_maintenance_schedule.auto_create ) {
      const leadTimeValue = this.asset_maintenance_schedule.auto_create_lead_time_value;

      if (leadTimeValue === null || typeof leadTimeValue === 'undefined' || leadTimeValue < 0) {
        this.utils.showFormValidationError('Auto Create Lead Time must be zero or greater.');
        return;
      }

      const validLeadTimeUnits = Object.keys(this.assetMaintenanceSchedulesService.AUTO_CREATE_LEAD_TIME_UNITS);

      if (!validLeadTimeUnits.includes(this.asset_maintenance_schedule.auto_create_lead_time_unit)) {
        this.utils.showFormValidationError('Please select a valid Auto Create Lead Time Unit.');
        return;
      }
    }

    this.inProgress = true;

    if( this.asset_maintenance_schedule.maintenance_type !== 'other' ){
      this.asset_maintenance_schedule.maintenance_type_other = null;
    }

    if( this.asset_maintenance_schedule.is_recurring ){
      this.asset_maintenance_schedule.next_due_at = null;
    } else {
      this.asset_maintenance_schedule.rrule = null;
      this.asset_maintenance_schedule.schedule_start_at = null;
      this.asset_maintenance_schedule.schedule_end_at = null;

      // Convert the selected Next Due At date and time to a unix timestamp.
      this.asset_maintenance_schedule.next_due_at = this.getNextDueAtTimestamp(
        this.nextDueAtDateTime
      );
    }

    if ( !this.asset_maintenance_schedule.auto_create ) {
      this.asset_maintenance_schedule.auto_create_lead_time_unit = "days";
      this.asset_maintenance_schedule.auto_create_lead_time_value = 0;
    }

    !this.asset_maintenance_schedule.id ? this.create(autoCloseDialog) : this.update(autoCloseDialog);
  }

  /**
   * Updates the Asset Maintenance Schedule with new data.
   *
   * @param {boolean} [autoCloseDialog=true] An optional param to indicate if the dialog should be closed or not.
   * @return void
   */
  private update(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('put', `v2/assets/${this.asset_maintenance_schedule.asset_id}/maintenance-schedules/${this.asset_maintenance_schedule.id}`, this.asset_maintenance_schedule)
      .then((response) => {
        this.asset_maintenance_schedule = response.data as AssetMaintenanceScheduleModel;

        this.onSuccess(`Updated: "${this.asset_maintenance_schedule.title}"`, autoCloseDialog);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  /**
   * Creates a new Asset Maintenance Schedule.
   *
   * @param {boolean} [autoCloseDialog=true] An optional param to indicate if the dialog should be closed or not.
   * @return {void}
   */
  private create(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('post', `v2/assets/${this.asset_maintenance_schedule.asset_id}/maintenance-schedules`, this.asset_maintenance_schedule)
      .then((response) => {
        this.asset_maintenance_schedule = response.data as AssetMaintenanceScheduleModel;

        this.onSuccess("New Asset Maintenance Schedule created.", autoCloseDialog);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  /**
   * Handles the success scenario by showing a toast message with the given message and closing the dialog if autoCloseDialog is true.
   *
   * @param {string} message - The message to display in the toast.
   * @param {boolean} [autoCloseDialog=true] - Whether the dialog should be automatically closed. Default value is true.
   * @return {void}
   */
  private onSuccess(message: string, autoCloseDialog: boolean = true): void {
    this.utils.showToast(message);

    // Check if the dialog should be closed.
    if ( autoCloseDialog ) {
      this.dialogRef.close(this.asset_maintenance_schedule);
    }
  }

  /**
   * Opens the SchedulerComponent dialog with preconfigured values and options.
   * The dialog allows modification of scheduling details such as all-day status,
   * duration, recurrence rule (rrule), start time, and end time. If the dialog
   * returns a response, the asset maintenance schedule is updated accordingly
   * based on the fields provided in the response.
   *
   * @return {void} Does not return a value.
   */
  onScheduler(): void {
    this.utils.showComponentDialog(
      SchedulerComponent,
      {
        value: {
          is_all_day: this.asset_maintenance_schedule.is_all_day,
          duration_minutes: this.asset_maintenance_schedule.duration_minutes,
          rrule: this.asset_maintenance_schedule.rrule,
          schedule_start_at: this.asset_maintenance_schedule.schedule_start_at,
          schedule_end_at: this.asset_maintenance_schedule.schedule_end_at
        } as SchedulerValue,
        title: this.asset_maintenance_schedule.title,
        timezone: this.app.account.timezone
      },
      { width: '90%' },
      (response: any) => {
        if ( typeof response != 'undefined' ) {
          this.asset_maintenance_schedule.is_all_day = typeof response.is_all_day !== 'undefined'
            ? response.is_all_day
            : this.asset_maintenance_schedule.is_all_day;

          this.asset_maintenance_schedule.duration_minutes = typeof response.duration_minutes !== 'undefined'
            ? response.duration_minutes
            : this.asset_maintenance_schedule.duration_minutes;

          // Synchronise duration fields with the updated Scheduler duration.
          const duration = this.schedulerService.getDurationFormValue(
            this.asset_maintenance_schedule.duration_minutes
          );
          this.duration_value = duration.value;
          this.duration_unit = duration.unit;

          this.asset_maintenance_schedule.rrule = typeof response.rrule !== 'undefined'
            ? response.rrule
            : this.asset_maintenance_schedule.rrule;

          this.asset_maintenance_schedule.schedule_start_at = typeof response.schedule_start_at !== 'undefined'
            ? response.schedule_start_at
            : this.asset_maintenance_schedule.schedule_start_at;

          this.asset_maintenance_schedule.schedule_end_at = typeof response.schedule_end_at !== 'undefined'
            ? response.schedule_end_at
            : this.asset_maintenance_schedule.schedule_end_at;
        }
      }
    );
  }

  /**
   * Generates and returns a summary of the asset maintenance schedule.
   *
   * The summary includes details based on recurrence, start time, end time,
   * timezone, and whether the event covers the entire day. If the schedule is
   * non-recurring or lacks sufficient data, an empty string is returned.
   *
   * @return {string} A formatted summary of the asset maintenance schedule or an empty string if the schedule is invalid.
   */
  get scheduleSummary(): string {
    if (
      !this.asset_maintenance_schedule.is_recurring ||
      !this.asset_maintenance_schedule.rrule ||
      !this.asset_maintenance_schedule.schedule_start_at
    ) {
      return '';
    }

    const schedulerValue: SchedulerValue = {
      is_all_day: !!this.asset_maintenance_schedule.is_all_day,
      duration_minutes: this.asset_maintenance_schedule.duration_minutes,
      rrule: this.asset_maintenance_schedule.rrule,
      schedule_start_at: this.asset_maintenance_schedule.schedule_start_at,
      schedule_end_at: this.asset_maintenance_schedule.schedule_end_at
    };

    const form: SchedulerFormValue = this.schedulerService.toSchedulerFormValue(schedulerValue);

    const timezone = this.app.account.timezone;

    let summary = this.schedulerService.getSummary(form, timezone);

    const start = moment.unix(this.asset_maintenance_schedule.schedule_start_at).tz(timezone);

    summary += this.asset_maintenance_schedule.is_all_day
      ? ` | Starts ${start.format('D MMM YYYY z')}`
      : ` | Starts ${start.format('D MMM YYYY, h:mm A z')}`;

    if (this.asset_maintenance_schedule.schedule_end_at) {
      const end = moment.unix(this.asset_maintenance_schedule.schedule_end_at).tz(timezone);

      summary += this.asset_maintenance_schedule.is_all_day
        ? ` | Ends ${end.format('D MMM YYYY z')}`
        : ` | Ends ${end.format('D MMM YYYY, h:mm A z')}`;
    }

    return summary;
  }

  /**
   * Determines whether the recurring schedule is valid based on the
   * asset maintenance schedule configuration.
   *
   * The schedule is considered valid if:
   * - The schedule is not marked as recurring.
   * - If recurring, the schedule has both a recurrence rule (rrule)
   *   and a valid start date (schedule_start_at).
   * - If an end date (schedule_end_at) is provided, it must not be
   *   earlier than the start date.
   *
   * @return {boolean} True if the recurring schedule is valid, false otherwise.
   */
  get isRecurringScheduleValid(): boolean {
    if ( !this.asset_maintenance_schedule.is_recurring ) {
      return true;
    }

    if (!this.asset_maintenance_schedule.rrule || !this.asset_maintenance_schedule.schedule_start_at) {
      return false;
    }

    if (
      this.asset_maintenance_schedule.schedule_end_at &&
      this.asset_maintenance_schedule.schedule_end_at < this.asset_maintenance_schedule.schedule_start_at
    ) {
      return false;
    }

    return true;
  }

  /**
   * Determines whether the next due date for maintenance is valid.
   * If the maintenance schedule is recurring, the method will always return true.
   * Otherwise, it checks for the existence of a valid `nextDueAtDateTime`.
   *
   * @return {boolean} Returns true if the maintenance schedule is recurring or if a valid next due date exists; otherwise, false.
   */
  get isNextDueAtValid(): boolean {
    if ( this.asset_maintenance_schedule.is_recurring ) {
      return true;
    }

    return moment.isMoment(this.nextDueAtDateTime) && this.nextDueAtDateTime.isValid();
  }

  /**
   * Determines if the auto-create configuration for the asset maintenance schedule is valid.
   *
   * The method checks the following:
   * - If auto-create is disabled, it returns true.
   * - If auto-create is enabled, it verifies that the lead time value is a non-negative number
   *   and that a lead time unit is specified.
   *
   * @return {boolean} Returns true if the auto-create configuration is valid, false otherwise.
   */
  get isAutoCreateValid(): boolean {
    if ( !this.asset_maintenance_schedule.auto_create ) {
      return true;
    }

    const leadTimeValue = this.asset_maintenance_schedule.auto_create_lead_time_value;

    return (
      leadTimeValue !== null &&
      typeof leadTimeValue !== 'undefined' &&
      leadTimeValue >= 0 &&
      !!this.asset_maintenance_schedule.auto_create_lead_time_unit
    );
  }

  /**
   * Handles changes to the "All Day" setting for a non-recurring
   * Asset Maintenance Schedule.
   *
   * Recalculates the Next Due At timestamp based on the currently
   * selected date and time. When "All Day" is enabled, the timestamp
   * is moved to the start of the selected day while preserving the
   * original DateTime value so that the user's selected time is
   * restored if "All Day" is disabled again.
   *
   * @return {void}
   */
  onAllDayChange(): void {
    if ( this.asset_maintenance_schedule.is_recurring ) {
      return;
    }

    this.asset_maintenance_schedule.next_due_at = this.getNextDueAtTimestamp(this.nextDueAtDateTime);

    if ( this.asset_maintenance_schedule.is_all_day ) {
      // Clear the stored duration but preserve the input value when toggling All Day.
      this.asset_maintenance_schedule.duration_minutes = null;
    } else {
      // Restore the duration using the previously selected value and unit.
      this.onDurationChange();
    }
  }

  /**
   * Calculates the Next Due At timestamp for a non-recurring
   * Asset Maintenance Schedule.
   *
   * When the schedule is marked as All Day, the timestamp is aligned
   * to the start of the selected day in the Account timezone.
   *
   * @param {moment.Moment | null} dateTime - The selected Next Due At date and time.
   * @return {number | null} The Unix timestamp in seconds, or null if no valid date is selected.
   */
  private getNextDueAtTimestamp(dateTime: moment.Moment | null): number | null {
    if ( !moment.isMoment(dateTime) || !dateTime.isValid() ) {
      return null;
    }

    const value: moment.Moment =
      moment.tz(
        dateTime.format('M/D/YYYY, h:mm:ss a'),
        'M/D/YYYY, h:mm:ss a',
        this.app.account.timezone
      );

    if (!value.isValid()) {
      return null;
    }

    if ( this.asset_maintenance_schedule.is_all_day ) {
      value.startOf('day');
    }

    return value.unix();
  }

  /**
   * Select contractor to associate with this asset maintenance schedule.
   *
   * @return {void}
   */
  onSelectContractor(): void {
    this.utils.showComponentDialog(
      NetworkedAccountsSelectorComponent,
      {
        selected: [this.asset_maintenance_schedule.contractor_account_id],
        multiple: false,
        can_clear_close: true
      },
      {
        width: '1200px'
      },
      (selected_contractor_account_id: number) => {
        if ( typeof selected_contractor_account_id != 'undefined' ) {
          this.asset_maintenance_schedule.contractor_account_id = selected_contractor_account_id > 0 ? selected_contractor_account_id : null;
        }
      }
    );
  }

  /**
   * Select the vendor the asset maintenance schedule should be associated with.
   *
   * @return {void}
   */
  onSelectVendor(): void {
    this.utils.showComponentDialog(
      VendorsSelectorComponent,
      {
        selected: [this.asset_maintenance_schedule.vendor_id],
        multiple: false,
        required: false
      },
      {
        width: '1200px'
      },
      (selected_vendor_id: number) => {
        if ( typeof selected_vendor_id != 'undefined' ) {
          this.asset_maintenance_schedule.vendor_id = selected_vendor_id > 0 ? selected_vendor_id : null;
        }
      }
    );
  }

  /**
   * Select users to associate with this asset maintenance schedule.
   *
   * @return {void}
   */
  onSelectUsers(): void {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.asset_maintenance_schedule.user_ids,
        multiple: true,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (results: any) => {
        if (typeof results !== 'undefined') {
          // Set the newly selected ids.
          this.asset_maintenance_schedule.user_ids = results;
        }
      }
    );
  }

  /**
   * Retrieves the details of the output date and time based on the provided event.
   *
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputNextDueDateTimeDetails(event: any): void {
    this.nextDueAtDateTime = event;
  }

  /**
   * Handles the change in duration by calculating and updating the duration in minutes
   * based on the currently set duration value and unit.
   * Updates the `duration_minutes` property of the `asset_maintenance_schedule` object.
   *
   * @return {void} This method does not return a value.
   */
  onDurationChange(): void {
    this.asset_maintenance_schedule.duration_minutes = this.schedulerService.calculateDurationMinutes(
      this.duration_value,
      this.duration_unit
    );
  }

  /**
   * Handles the duration value by validating and rounding the value to the
   * nearest whole number. When called from the input blur event, the displayed
   * value is also normalized to the rounded integer.
   *
   * @param {FocusEvent|null} event The optional input blur event.
   * @return {void} No value is returned.
   */
  onDurationValueBlur(event: FocusEvent | null = null): void {
    const value = this.duration_value;

    if (value === null || value === undefined) {
      return;
    }

    const numericValue = Number(value);

    if (isNaN(numericValue)) {
      return;
    }

    const roundedValue = Math.max(1, Math.round(numericValue));

    this.duration_value = roundedValue;

    if ( event?.target ) {
      (event.target as HTMLInputElement).value = roundedValue.toString();
    }

    this.onDurationChange();
  }

  /**
   * Handles the auto create lead time value by validating and rounding the value to two
   * decimal places. When called from the input blur event, the displayed value
   * is also normalized to remove unnecessary trailing zeros.
   *
   * @param {FocusEvent|null} event The optional input blur event.
   * @return {void} No value is returned.
   */
  onAutoCreateLeadTimeValueBlur(event: FocusEvent | null = null): void {
    const value = this.asset_maintenance_schedule.auto_create_lead_time_value;

    if (value === null || value === undefined) {
      return;
    }

    const numericValue = Number(value);

    if (isNaN(numericValue)) {
      return;
    }

    const roundedValue = Math.round((numericValue + Number.EPSILON) * 100) / 100;

    this.asset_maintenance_schedule.auto_create_lead_time_value = roundedValue;

    if ( event?.target ) {
      (event.target as HTMLInputElement).value = roundedValue.toString();
    }
  }
}
